/**
 * Composable открытия Р7 для строки FILEATTACHMENTS (WUI-305 + WUI-316 + WUI-492).
 *
 * - `prepare` / reuse → `registerR7OfficeDocumentContext` + `registerEditingSession`
 *   (и для dialog, и для inline preview — общий timeout awaiting_ws)
 * - open **не** меняет DS-фазу бейджа (`preOpenDsPhase` на сессии)
 * - dirty / close — `r7AttachmentEditorLifecycle.ts`
 *
 * Закрытие модалки — `ElementRenderer.vue`; preview — `useR7InlinePreview.ts`.
 *
 * @see dev/contracts/attachments.md — «Фазы бейджа: open / dirty / close»
 */
import { useR7DocEditor } from '@/common/composables/useR7DocEditor'
import type { FileItem } from '@/common/types/fileItem'
import { IKey } from '@/common/constants/ikey'
import { resolveAttachmentDisplayFileName } from '@/common/utils/attachmentRpcPolicy'
import { isOfficeFileName } from '@/common/utils/attachmentPreviewUtils'
import {
  ensureAttachmentClientRowKey,
  getAttachmentClientRowKey,
  MIDDLEWARE_DOCUMENT_SESSION_ID_FIELD,
  resolveAttachmentRowCodeVersion,
  buildAttachmentPrepareRequestFromItem,
  officeStagingMetaFromPrepareResponse,
  sanitizePrepareFieldMapForStagingReuse,
  type PlatformDownloadMeta,
  type TypedPlatformField,
} from '@/common/utils/fileAttachmentsUniData'
import { getDsAttachmentStore } from '@/common/composables/useDsAttachmentState'
import {
  registerR7EditorOfficeCallback,
  type R7EditorOfficeCallbackHandlers,
} from '@/common/utils/r7EditorOfficeCallbackSession'
import { registerEditingSession } from '@/common/utils/r7AttachmentEditSession'
import { capturePreOpenDsPhase } from '@/common/utils/r7AttachmentEditorLifecycle'
import { registerR7OfficeDocumentContext } from '@/common/utils/r7OfficeDocumentContextRegistry'
import { resolveAttachmentDocumentSessionId } from '@/common/utils/dsAttachmentSnapshot'
import { prepareOfficeAttachmentEditor } from '@/services/r7AttachmentEditor.service'
import type { PlatformFileSessionContext } from '@/common/utils/platformFileSession'
import { useNotification } from '@/common/composables/useNotification'
import { tGlobal } from '@/common/composables/useI18n'
import { resolveR7DocEditorMode } from '@/common/types/r7DocEditorInitConfig'

export function isOfficeAttachmentFileName(fileName: string): boolean {
  return isOfficeFileName(fileName)
}

export type AttachmentR7EditorOpenParams = {
  item: FileItem
  elementId: number
  elementName: string
  session: PlatformFileSessionContext
  mode?: 'edit' | 'view'
  socketId?: string | null
}

export type AttachmentR7StagedResult = {
  storageUri: string
  changed: boolean
}

export type AttachmentR7EditorOpenHandle = {
  unregisterOfficeCallback: () => void
  documentKey: string
  documentSessionId: string
}

export function useAttachmentR7Editor(placeholderId: string) {
  const editor = useR7DocEditor(placeholderId)
  const notification = useNotification()
  let unregisterOfficeCallback: (() => void) | null = null

  function clearOfficeCallbackRegistration() {
    unregisterOfficeCallback?.()
    unregisterOfficeCallback = null
  }

  async function openAttachment(
    params: AttachmentR7EditorOpenParams,
    options?: {
      /** Temp file из preview prepare — повторный platform read не нужен. */
      stagingStorageUri?: string
      /** Для reopen по storage_uri: id DS-сессии, иначе middleware может требовать file id. */
      stagingDocumentSessionId?: string
      onStaged?: (result: AttachmentR7StagedResult) => void
      onPrepared?: (result: { storageUri: string; documentKey: string }) => void
      /** Сохранить полный staging meta в row + DS (после prepare или reuse snapshot). */
      onStagingMetaPersist?: (item: FileItem, meta: PlatformDownloadMeta) => void
      onEditorEvent?: (name: string, payload: unknown) => void
      /** Отмена init после async prepare (закрытие preview / открытие модального редактора). */
      shouldAbort?: () => boolean
      /** Отмена HTTP prepare при смене формы / sessionId. */
      signal?: AbortSignal
    },
  ): Promise<AttachmentR7EditorOpenHandle> {
    clearOfficeCallbackRegistration()

    const fileName = resolveAttachmentDisplayFileName(params.item)
    if (!fileName || !isOfficeAttachmentFileName(fileName)) {
      throw new Error(tGlobal('attachments.r7OfficeOnly'))
    }

    const rowCv = resolveAttachmentRowCodeVersion(
      (params.item.fields ?? {}) as Record<string, unknown>,
    )
    const code = rowCv?.code ?? 0
    const version = rowCv?.version ?? 0
    const editorMode = params.mode ?? 'edit'
    const preOpenDsPhase = capturePreOpenDsPhase(params.item, params.elementId)

    const dsStore = getDsAttachmentStore(params.elementId)
    const existingSnapshot = dsStore.getSnapshot(params.item)
    const clientRowKey =
      getAttachmentClientRowKey((params.item.fields ?? {}) as Record<string, unknown>)
      ?? existingSnapshot?.clientRowKey
      ?? ensureAttachmentClientRowKey(params.item)
    const snapshotInitConfig = existingSnapshot?.docEditorInitConfig
    const snapshotInitKey = snapshotInitConfig?.document?.key?.trim() ?? ''
    const snapshotDocumentKey = existingSnapshot?.documentKey?.trim() ?? ''
    const snapshotMode = resolveR7DocEditorMode(snapshotInitConfig)
    // Reuse только если init.key === актуальный documentKey (после WS key меняется).
    const modeCompatible =
      snapshotMode == null
      || snapshotMode === editorMode
      || (snapshotMode === 'edit' && editorMode === 'view')
    const canReuseSnapshot =
      dsStore.getPhase(params.item) !== 'none'
      && existingSnapshot != null
      && Boolean(existingSnapshot.callbackUrl?.trim())
      && Boolean(existingSnapshot.documentSessionId?.trim())
      && snapshotInitConfig != null
      && snapshotInitKey !== ''
      && snapshotInitKey === snapshotDocumentKey
      && modeCompatible

    let prepared: Awaited<ReturnType<typeof prepareOfficeAttachmentEditor>>

    if (canReuseSnapshot && existingSnapshot && snapshotInitConfig) {
      prepared = {
        documentKey: existingSnapshot.documentKey,
        documentUrl: existingSnapshot.documentUrl,
        callbackUrl: existingSnapshot.callbackUrl,
        token: existingSnapshot.token,
        docEditorInitConfig: snapshotInitConfig,
        storageUri: existingSnapshot.storageUri,
        documentSessionId: existingSnapshot.documentSessionId,
        generation: existingSnapshot.generation,
        dsDirty: existingSnapshot.dsDirty,
      }
      options?.onStagingMetaPersist?.(
        params.item,
        officeStagingMetaFromPrepareResponse(prepared),
      )
      // Бейдж: open не меняет DS-фазу.
    } else {
      if (options?.shouldAbort?.()) {
        return {
          unregisterOfficeCallback: clearOfficeCallbackRegistration,
          documentKey: '',
          documentSessionId: '',
        }
      }

      const { request: prepareRequest, prepareFile: rawPrepareFile } =
        buildAttachmentPrepareRequestFromItem({
          elementId: params.elementId,
          elementName: params.elementName,
          session: params.session,
          item: params.item,
          mode: editorMode,
          socketId: params.socketId ?? null,
        })

      const stagingStorageUri = String(
        options?.stagingStorageUri
        ?? existingSnapshot?.storageUri
        ?? '',
      ).trim()
      const stagingDocumentSessionId = String(
        options?.stagingDocumentSessionId
        ?? existingSnapshot?.documentSessionId
        ?? resolveAttachmentDocumentSessionId(params.item, params.elementId)
        ?? '',
      ).trim()
      // После staging: idempotent prepare без File (иначе новый UUID-сеанс).
      const prepareFile =
        stagingDocumentSessionId || stagingStorageUri ? undefined : rawPrepareFile

      if (stagingStorageUri || stagingDocumentSessionId) {
        const currentData = prepareRequest.currentData as
          | { fields?: { fields?: Record<string, { type?: string; value?: unknown }> } }
          | undefined
        const fieldMap = currentData?.fields?.fields
        if (fieldMap) {
          if (stagingStorageUri) {
            fieldMap[IKey.STORAGE_URI] = { type: 'String', value: stagingStorageUri }
          }
          if (stagingDocumentSessionId) {
            fieldMap[MIDDLEWARE_DOCUMENT_SESSION_ID_FIELD] = {
              type: 'String',
              value: stagingDocumentSessionId,
            }
          }
          sanitizePrepareFieldMapForStagingReuse(fieldMap as Record<string, TypedPlatformField>)
        }
      }

      if (options?.shouldAbort?.()) {
        return {
          unregisterOfficeCallback: clearOfficeCallbackRegistration,
          documentKey: '',
          documentSessionId: '',
        }
      }

      prepared = await prepareOfficeAttachmentEditor(prepareRequest, prepareFile, {
        signal: options?.signal,
      })
      options?.onStagingMetaPersist?.(
        params.item,
        officeStagingMetaFromPrepareResponse(prepared),
      )
      dsStore.upsertFromPrepare(params.item, {
        documentKey: prepared.documentKey,
        storageUri: prepared.storageUri,
        documentUrl: prepared.documentUrl,
        callbackUrl: prepared.callbackUrl,
        token: prepared.token,
        docEditorInitConfig: prepared.docEditorInitConfig,
        documentSessionId: prepared.documentSessionId,
        generation: prepared.generation,
        dsDirty: prepared.dsDirty,
        clientRowKey,
        platformCode: code > 0 ? code : undefined,
        platformVersion: code > 0 ? version : undefined,
      })
      // Не setPhase: upsert сохраняет staged/awaiting_ws/editing.
    }

    registerR7OfficeDocumentContext(prepared.documentKey, {
      elementId: params.elementId,
      code,
      version,
      storageUri: prepared.storageUri,
      fileName,
    })

    options?.onPrepared?.({ storageUri: prepared.storageUri, documentKey: prepared.documentKey })

    registerEditingSession({
      elementId: params.elementId,
      code,
      version,
      documentKey: prepared.documentKey,
      documentSessionId: prepared.documentSessionId,
      fileName,
      initialStorageUri: prepared.storageUri,
      mode: editorMode,
      preOpenDsPhase,
    })

    if (editorMode === 'edit') {
      const officeHandlers: R7EditorOfficeCallbackHandlers = {
        elementId: params.elementId,
        documentSessionId: prepared.documentSessionId,
        documentKey: prepared.documentKey,
        onEditorEvent: options?.onEditorEvent,
        onStaged: options?.onStaged,
        onSaveError: () => {
          notification.error({
            summary: tGlobal('attachments.r7SaveErrorSummary'),
            detail: tGlobal('attachments.r7SaveErrorDetailNamed', { fileName }),
          })
        },
      }
      unregisterOfficeCallback = registerR7EditorOfficeCallback(officeHandlers)
    }

    if (options?.shouldAbort?.()) {
      return {
        unregisterOfficeCallback: clearOfficeCallbackRegistration,
        documentKey: prepared.documentKey,
        documentSessionId: prepared.documentSessionId,
      }
    }

    try {
      await editor.initEditor({
        docEditorInitConfig: prepared.docEditorInitConfig,
        token: prepared.token,
        onEditorEvent: (name, payload) => {
          options?.onEditorEvent?.(name, payload)
        },
      })
    } catch (error) {
      clearOfficeCallbackRegistration()
      throw error
    }

    return {
      unregisterOfficeCallback: clearOfficeCallbackRegistration,
      documentKey: prepared.documentKey,
      documentSessionId: prepared.documentSessionId,
    }
  }

  /**
   * Inline DocEditor в зоне превью MFileInput.
   * Тот же lifecycle, что у диалога (session + timeout awaiting_ws).
   */
  async function openAttachmentPreview(
    params: Omit<AttachmentR7EditorOpenParams, 'mode'> & { mode?: 'edit' | 'view' },
    options?: {
      stagingStorageUri?: string
      stagingDocumentSessionId?: string
      onPrepared?: (result: { storageUri: string; documentKey: string }) => void
      onStagingMetaPersist?: (item: FileItem, meta: PlatformDownloadMeta) => void
      onEditorEvent?: (name: string, payload: unknown) => void
      shouldAbort?: () => boolean
      signal?: AbortSignal
    },
  ): Promise<AttachmentR7EditorOpenHandle> {
    return openAttachment(
      { ...params, mode: params.mode ?? 'edit' },
      { ...options },
    )
  }

  function destroyEditorUi() {
    clearOfficeCallbackRegistration()
    editor.destroyEditor()
  }

  function finalizeEditorSession() {
    destroyEditorUi()
  }

  const destroyEditor = () => {
    destroyEditorUi()
  }

  const requestForceSave = (): boolean => editor.requestForceSave()

  return {
    isLoading: editor.isLoading,
    error: editor.error,
    destroyEditor,
    destroyEditorUi,
    finalizeEditorSession,
    openAttachment,
    openAttachmentPreview,
    requestForceSave,
  }
}
